import { Box, HStack } from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';
import { useEffect, useRef, useState } from 'react';

import Button from 'components/Button';
import Icon from 'components/Icon';
import Text from 'components/Text';

export const MERLIN_STREAMING_EVENT = 'merlin-streaming';

const borderShimmer = keyframes`
  0% { border-color: rgba(159, 122, 234, 0.4); }
  50% { border-color: rgba(237, 137, 54, 0.6); }
  100% { border-color: rgba(159, 122, 234, 0.4); }
`;

interface IVibeCodeButtonProps {
  isActive?: boolean;
  onClick: () => void;
  isDisabled?: boolean;
  showBubble?: boolean;
}

const VibeCodeButton = ({ isActive, onClick, isDisabled, showBubble }: IVibeCodeButtonProps) => {
  const [isMerlinStreaming, setIsMerlinStreaming] = useState(false);
  const [hasUnread, setHasUnread] = useState(!!showBubble);
  const wasStreamingRef = useRef(false);
  const isActiveRef = useRef(isActive);
  isActiveRef.current = isActive;

  const showNotification = (isMerlinStreaming || hasUnread) && !isActive;

  // Clear unread when user opens the panel
  useEffect(() => {
    if (isActive) {
      setHasUnread(false);
    }
  }, [isActive]);

  useEffect(() => {
    const handler = (e: CustomEvent) => {
      const streaming = e.detail.isStreaming;

      // When streaming ends while panel is closed, mark as unread
      if (wasStreamingRef.current && !streaming && !isActiveRef.current) {
        setHasUnread(true);
      }

      wasStreamingRef.current = streaming;
      setIsMerlinStreaming(streaming);
    };

    window.addEventListener(MERLIN_STREAMING_EVENT, handler as EventListener);
    return () => {
      window.removeEventListener(MERLIN_STREAMING_EVENT, handler as EventListener);
    };
  }, []);

  const shimmerAnimation = isDisabled || isActive ? 'none' : `${borderShimmer} 3s ease-in-out infinite`;

  return (
    <Box position="relative" display="inline-flex">
      <Button
        scheme="outline"
        onClick={onClick}
        isDisabled={isDisabled}
        sx={{
          borderColor: isActive ? 'purple.400' : 'rgba(159, 122, 234, 0.4)',
          bg: isActive ? 'purple.900' : 'transparent',
          animation: shimmerAnimation,
          _hover: {
            bg: isActive ? 'purple.800' : 'whiteAlpha.100',
            borderColor: 'purple.400',
            animation: 'none',
          },
        }}
      >
        <HStack spacing={1.5}>
          <Icon type="GenerateCodeSolution" size={16} />
          <Text fontSize="small">Vibe Code</Text>
        </HStack>
      </Button>
      {showNotification && (
        <Box
          position="absolute"
          top="-3px"
          right="-3px"
          w="8px"
          h="8px"
          borderRadius="full"
          bg="purple.400"
          boxShadow="0 0 6px rgba(159, 122, 234, 0.8)"
          animation="pulse 1.5s ease-in-out infinite"
        />
      )}
    </Box>
  );
};

export default VibeCodeButton;
